import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../../../sidebar/sidebar";
import Header from "../../../../header/header";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { KpiIcon } from "../KpiIcons";
import { injectStyles } from "./components/styles";
import { extractArray, isOk } from "./components/utils";
import useDebounce from "./components/useDebounce";
import StatCard from "./components/StatCard";
import ShimmerRow from "./components/ShimmerRow";
import HistoryRow from "./components/HistoryRow";
import ChatModal from "../../../components/chatComponent/ChatModal";
import { useCurrentUser } from "../../../../../hooks/useCurrentUser";

/* ─────────────────────────────────────────────────────────────
   Multi-select Location Dropdown (checkbox style)
───────────────────────────────────────────────────────────── */
const LocationMultiSelect = ({ locs, selectedIds, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const allSelected = selectedIds.length === locs.length || selectedIds.length === 0;

  const toggleAll = () => {
    onChange(locs.map((l) => l.id));
  };

  const toggleOne = (id) => {
    if (selectedIds.includes(id)) {
      const next = selectedIds.filter((x) => x !== id);
      onChange(next.length === 0 ? locs.map((l) => l.id) : next);
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const label = (() => {
    if (!locs.length) return "All Locations";
    if (allSelected || selectedIds.length === locs.length) return "All Locations";
    if (selectedIds.length === 1) {
      return locs.find((l) => l.id === selectedIds[0])?.name || "1 Location";
    }
    return `${selectedIds.length} Locations`;
  })();

  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        className="ahp-sel"
        onClick={() => setOpen((o) => !o)}
        style={{
          height: 36,
          display: "flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          background: "#fff",
          textAlign: "left",
          minWidth: 150,
          justifyContent: "space-between",
          padding: "0 10px",
        }}
      >
        <span style={{ fontSize: 11.5, color: "var(--ink2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </span>
        <KpiIcon name="search" size={10} color="var(--ink4)" />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 999,
            background: "#fff",
            border: "1px solid var(--bdr)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.10)",
            minWidth: 210,
            maxHeight: 260,
            overflowY: "auto",
            padding: "6px 0",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 13px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              color: "var(--ink2)",
              background: allSelected ? "var(--blue3)" : "transparent",
              borderBottom: "1px solid var(--bdr2)",
            }}
          >
            <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ accentColor: "var(--blue)", width: 13, height: 13 }} />
            All Locations
          </label>

          {locs.map((l) => {
            const checked = selectedIds.includes(l.id);
            return (
              <label
                key={l.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 13px",
                  cursor: "pointer",
                  fontSize: 11.5,
                  color: "var(--ink2)",
                  background: checked ? "var(--blue3)" : "transparent",
                }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggleOne(l.id)} style={{ accentColor: "var(--blue)", width: 13, height: 13 }} />
                {l.name}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Page
───────────────────────────────────────────────────────────── */
const AnswerHistoryPage = (props) => {
  const { qid: questionId } = useParams();
  const navState = JSON.parse(
    sessionStorage.getItem(`ahp_state_${questionId}`) || "{}"
  );

  useEffect(() => {
    injectStyles();
  }, []);

  useEffect(() => {
    return () => {
      sessionStorage.removeItem(`ahp_state_${questionId}`);
    };
  }, [questionId]);

  const { currentUserId } = useCurrentUser();
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");

  const currentUserName = currentUser?.data?.user?.dataValues
    ? `${currentUser.data.user.dataValues.first_name ?? ""} ${currentUser.data.user.dataValues.last_name ?? ""}`.trim()
    : `${currentUser?.first_name ?? ""} ${currentUser?.last_name ?? ""}`.trim() || "Unknown";

  const canAddAndEditRecords = navState?.assignedTo?.some(
    (user) => user.id == currentUserId
  );

  const unitSetting = navState?.unitSetting ?? null;

  const navHasLocations =
    Array.isArray(navState.locations) && navState.locations.length > 0;

  /* ── Core state ── */
  const [sidebar, setSidebar] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [kpiName, setKpiName] = useState(navState.kpiName || "");
  const [fys, setFys] = useState([]);

  const [locs, setLocs] = useState(navHasLocations ? navState.locations : []);
  const locsLoadedRef = useRef(navHasLocations);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ── Filters ── */
  const [fyId, setFyId] = useState(
    navState.financialYearId ? String(navState.financialYearId) : ""
  );
  const [locationIds, setLocationIds] = useState(
    navHasLocations ? navState.locations.map((l) => l.id) : []
  );
  const [fromDate, setFromDate] = useState(navState.fromDate?.slice(0, 10) || "");
  const [toDate, setToDate] = useState(navState.toDate?.slice(0, 10) || "");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const dSearch = useDebounce(search, 350);
  const hasFilters = !!(fyId || fromDate || toDate || search || status);

  /* ── Chat state ── */
  const [chatItem, setChatItem] = useState(null);
  const [chatParticipants, setChatParticipants] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatUserForRole, setChatUserForRole] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);

  /* ─────────────────────────────────────────────────────────────
     FETCH LOCATIONS
  ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (navHasLocations) return;

    apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET")
      .then((res) => {
        if (isOk(res)) {
          const raw = extractArray(res);
          const mapped = raw.map((l) => ({
            id: l.id,
            name: l.unitCode || l.location?.city || l.name || `Location ${l.id}`,
          }));
          setLocs(mapped);
          setLocationIds(mapped.map((l) => l.id));
          locsLoadedRef.current = true;
        }
      })
      .catch((err) => {
        console.error("[Locs] failed to load:", err);
        locsLoadedRef.current = true;
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─────────────────────────────────────────────────────────────
     HELPER — get current/latest FY id
  ───────────────────────────────────────────────────────────── */
  const getCurrentFyId = useCallback(
    (fysList) => {
      if (!fysList || fysList.length === 0) return "";
      if (navState.financialYearId) return String(navState.financialYearId);
      return String(fysList[fysList.length - 1].id);
    },
    [navState.financialYearId]
  );

  /* ─────────────────────────────────────────────────────────────
     DATA LOADER
  ───────────────────────────────────────────────────────────── */
  const loadData = useCallback(
    async ({ fyIdParam, locationIdsParam, fromDateParam, toDateParam } = {}) => {
      if (!questionId) {
        setError("No question ID in the URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      try {
        const fyRes = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`,
          {},
          {},
          "GET"
        );

        let resolvedFyId = fyIdParam;
        let fysList = [];
        if (isOk(fyRes)) {
          fysList = extractArray(fyRes);
          setFys(fysList);
          if (!resolvedFyId) {
            resolvedFyId = getCurrentFyId(fysList);
            setFyId(resolvedFyId);
          }
        }

        const params = { questionId };
        if (resolvedFyId) params.financial_year_id = resolvedFyId;

        const lIdsToSend =
          locationIdsParam && locationIdsParam.length > 0
            ? locationIdsParam
            : locationIds.length > 0
            ? locationIds
            : locs.map((l) => l.id);

        if (lIdsToSend.length > 0) params.locations = lIdsToSend;
        if (fromDateParam) params.from_date = fromDateParam;
        if (toDateParam) params.to_date = toDateParam;

        const histRes = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}quantative-data`,
          {},
          params,
          "GET"
        );

        if (isOk(histRes)) setAnswers(extractArray(histRes));
        else
          setError(
            `Failed to load: ${histRes?.data?.message || histRes?.message || "Unknown error"}`
          );
      } catch (err) {
        setError(`Network error: ${err?.message || String(err)}`);
      } finally {
        setLoading(false);
      }
    },
    [questionId, getCurrentFyId, locationIds, locs]
  );

  /* ── Initial load ── */
  useEffect(() => {
    if (navHasLocations) {
      loadData({
        fyIdParam: fyId,
        locationIdsParam: locationIds,
        fromDateParam: fromDate,
        toDateParam: toDate,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Load after locs API resolves ── */
  useEffect(() => {
    if (navHasLocations) return;
    if (locs.length === 0) return;
    loadData({
      fyIdParam: fyId,
      locationIdsParam: locs.map((l) => l.id),
      fromDateParam: fromDate,
      toDateParam: toDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locs]);

  /* ── Re-load on filter change ── */
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadData({
      fyIdParam: fyId,
      locationIdsParam: locationIds,
      fromDateParam: fromDate,
      toDateParam: toDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fyId, locationIds, fromDate, toDate]);

  /* ─────────────────────────────────────────────────────────────
     CHAT
  ───────────────────────────────────────────────────────────── */
  const loadChatData = useCallback(
    async (answer) => {
      setChatLoading(true);
      setChatParticipants([]);
      setChatMessages([]);
      setChatUserForRole(null);

      const chatFyId = navState.financialYearId ?? answer.financial_year_id;
      const sourceId = answer.location_id ?? answer.locationId ?? null;
      const subLocId = answer.sub_location_id ?? answer.subLocationId ?? null;
      const aFromDate = answer.from_date ?? answer.fromDate ?? null;
      const aToDate = answer.to_date ?? answer.toDate ?? null;
      const dataOwnerId =
        answer.createdBy?.id ?? answer.created_by ?? answer.userId ?? null;

      try {
        const participantsRes = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}reporting/chats/participants`,
          {},
          {
            dataOwnerId,
            financialYearId: chatFyId,
            questionId: Number(questionId),
            sourceId,
            subLocationId: subLocId,
            fromDate: aFromDate,
            toDate: aToDate,
          },
          "GET"
        );

        if (isOk(participantsRes)) {
          const raw = extractArray(participantsRes);
          setChatParticipants(
            raw.map((p) => ({
              id: p.id,
              name: p.name,
              email: p.email || "",
              first_name: p.first_name || "",
              last_name: p.last_name || "",
            }))
          );
        }

        const messagesRes = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}reporting/chats`,
          {},
          {
            currentUserId,
            financialYearId: chatFyId,
            questionId: Number(questionId),
            sourceId,
            subLocationId: subLocId,
            fromDate: aFromDate,
            toDate: aToDate,
          },
          "GET"
        );

        if (isOk(messagesRes)) {
          const resData = messagesRes?.data ?? messagesRes;
          const chats = resData?.chats ?? extractArray(messagesRes);
          const userRole = resData?.user ?? null;
          setChatMessages(chats);
          setChatUserForRole(userRole);
        }
      } catch (err) {
        console.error("[ChatLoad] failed:", err);
      } finally {
        setChatLoading(false);
      }
    },
    [questionId, currentUserId, navState.financialYearId]
  );

  const handleOpenChat = useCallback(
    (answer) => {
      setChatItem(answer);
      loadChatData(answer);
    },
    [loadChatData]
  );

  const handleCloseChat = useCallback(() => {
    setChatItem(null);
    setChatParticipants([]);
    setChatMessages([]);
    setChatUserForRole(null);
  }, []);

  const handleSendMessage = useCallback(
    async (newMessage) => {
      if (!chatItem) return;

      const chatFyId = navState.financialYearId ?? chatItem.financial_year_id;
      const sourceId = chatItem.location_id ?? chatItem.locationId ?? null;
      const subLocId = chatItem.sub_location_id ?? chatItem.subLocationId ?? null;
      const aFromDate = chatItem.from_date ?? chatItem.fromDate ?? null;
      const aToDate = chatItem.to_date ?? chatItem.toDate ?? null;
      const dataOwnerId =
        chatItem.createdBy?.id ?? chatItem.created_by ?? chatItem.userId ?? null;

      const fyLabel =
        fys.find((fy) => String(fy.id) === String(chatFyId))
          ?.financial_year_value ?? String(chatFyId);

      const locationObj = locs.find((l) => String(l.id) === String(sourceId)) ?? {
        id: sourceId,
        parentId: sourceId,
      };

      const questionDetailsForMail = {
        financialYear: fyLabel,
        title: navState.kpiName || `KPI #${questionId}`,
        answerFrequency: chatItem.answer_frequency ?? chatItem.answerFrequency ?? null,
        questionFrequency: chatItem.frequency ?? null,
        fromDate: aFromDate,
        toDate: aToDate,
        location: locationObj,
      };

      const dataToSend = {
        content: newMessage.content,
        mentions: newMessage.mentions,
        questionId: Number(questionId),
        financialYearId: chatFyId,
        sourceId,
        subLocationId: subLocId,
        fromDate: aFromDate,
        toDate: aToDate,
        dataOwnerId,
        currentUserName,
        questionDetailsForMail,
      };

      const frequency = chatItem.frequency ?? null;
      if (frequency && frequency !== "CUSTOM") {
        delete dataToSend.fromDate;
        delete dataToSend.toDate;
      }

      try {
        await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}reporting/chats`,
          {},
          dataToSend,
          "POST",
          undefined,
          false
        );
      } catch (err) {
        console.error("[ChatSend] failed:", err);
      }
    },
    [chatItem, currentUserId, currentUserName, questionId, navState, fys, locs]
  );

  /* ── Client-side filters ── */
  const rows = answers.filter((a) => {
    if (
      dSearch &&
      !`${a.value ?? ""} ${a.from_date ?? ""} ${a.to_date ?? ""} ${a.note ?? ""}`
        .toLowerCase()
        .includes(dSearch.toLowerCase())
    )
      return false;
    if (status && (a.status || "pending").toLowerCase() !== status) return false;
    return true;
  });

  const total = rows.length;
  const accepted = rows.filter((a) => (a.status || "").toLowerCase() === "approved").length;
  const pending = rows.filter((a) => !a.status || a.status.toLowerCase() === "pending").length;
  const rejected = rows.filter((a) => (a.status || "").toLowerCase() === "rejected").length;

  const displayName = kpiName || (loading ? "" : `KPI #${questionId}`);

  const clearAll = () => {
    setFyId("");
    setLocationIds(locs.map((l) => l.id));
    setFromDate("");
    setToDate("");
    setSearch("");
    setStatus("");
  };

  const chatModalProps = chatItem
    ? {
        questionId: Number(questionId),
        financialYearId: navState.financialYearId ?? chatItem.financial_year_id,
        sourceId: chatItem.location_id ?? chatItem.locationId ?? null,
        subLocationId: chatItem.sub_location_id ?? chatItem.subLocationId ?? null,
        fromDate: chatItem.from_date ?? chatItem.fromDate ?? null,
        toDate: chatItem.to_date ?? chatItem.toDate ?? null,
      }
    : {};

  const chatTitle = chatItem
    ? (() => {
        const locName = locs.find(
          (l) => String(l.id) === String(chatItem.location_id ?? chatItem.locationId)
        )?.name;
        const fd = (chatItem.from_date ?? chatItem.fromDate ?? "")?.slice(0, 10);
        const td = (chatItem.to_date ?? chatItem.toDate ?? "")?.slice(0, 10);
        return ["Chat", locName ? `· ${locName}` : "", fd && td ? `· ${fd} → ${td}` : fd ? `· ${fd}` : ""]
          .filter(Boolean).join(" ");
      })()
    : "Chat";

  /* ─────────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────────── */
  return (
    <div style={{ display: "flex", background: "var(--bg)", minHeight: "100vh", fontFamily: "var(--ff)" }}>
      <div
        style={{
          flex: sidebar ? "0 0 21%" : "0 0 60px",
          position: "sticky",
          top: 0,
          height: "100vh",
          background: "#fff",
          zIndex: 100,
          transition: "flex .2s",
          borderRight: "1px solid var(--bdr)",
        }}
      >
        <Sidebar dataFromParent={props.location?.pathname} onSidebarToggle={setSidebar} />
      </div>

      <main style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
        <Header />

        {/* ── Page header ── */}
        <div style={{ background: "#fff", borderBottom: "1px solid var(--bdr)", padding: "18px 28px 0" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 7, marginBottom: 6 }}>
                <div style={{ width: 26, height: 26, minWidth: 26, minHeight: 26, maxWidth: 26, maxHeight: 26, borderRadius: 7, background: "var(--blue3)", border: "1px solid var(--blue2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                  <KpiIcon name="history" size={13} color="var(--blue)" />
                </div>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: "var(--blue)", textTransform: "uppercase", letterSpacing: ".06em", fontFamily: "var(--fm)" }}>
                  Answer History
                </span>
              </div>
              {loading ? (
                <div className="ahp-shimmer" style={{ height: 22, width: 320, marginBottom: 8 }} />
              ) : (
                <h1 style={{ margin: 0, fontSize: 21, fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.3, maxWidth: 620, wordBreak: "break-word" }}>
                  {displayName}
                </h1>
              )}
              {navState.module && (
                <span style={{ display: "inline-block", marginTop: 6, background: "var(--bdr2)", color: "var(--ink3)", padding: "2px 9px", borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                  {navState.module}
                </span>
              )}
            </div>

            {!loading && !error && (
              <div className="ahp-up" style={{ display: "flex", flexDirection: "row", gap: 8, flexShrink: 0, flexWrap: "wrap", alignItems: "center" }}>
                <StatCard iconName="history" label="Total" value={total} color="#3b82f6" bg="#eff6ff" border="#bfdbfe" />
                <StatCard iconName="check" label="Accepted" value={accepted} color="#16a34a" bg="#dcfce7" border="#bbf7d0" />
                <StatCard iconName="audit" label="Pending" value={pending} color="#d97706" bg="#fef3c7" border="#fde68a" />
                {rejected > 0 && (
                  <StatCard iconName="x" label="Rejected" value={rejected} color="#dc2626" bg="#fee2e2" border="#fecaca" />
                )}
              </div>
            )}
          </div>

          {/* ── Filters ── */}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", paddingBottom: 14 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", display: "flex", alignItems: "center" }}>
                <KpiIcon name="search" size={12} color="var(--ink4)" />
              </div>
              <input className="ahp-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" style={{ paddingLeft: 30, width: 180, height: 36 }} />
            </div>

            <select className="ahp-sel" value={fyId} onChange={(e) => setFyId(e.target.value)}>
              <option value="">All Financial Years</option>
              {fys.map((fy) => (
                <option key={fy.id} value={String(fy.id)}>{fy.financial_year_value || fy.name}</option>
              ))}
            </select>

            <LocationMultiSelect locs={locs} selectedIds={locationIds} onChange={setLocationIds} />

            <select className="ahp-sel" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="approved">Accepted</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input className="ahp-sel" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ height: 36, fontSize: 11.5 }} />
              <span style={{ color: "var(--ink4)", fontSize: 12, fontWeight: 600 }}>–</span>
              <input className="ahp-sel" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ height: 36, fontSize: 11.5 }} />
            </div>

            {hasFilters && (
              <button className="ahp-btn ahp-btn-g" onClick={clearAll} style={{ fontSize: 11.5 }}>
                <KpiIcon name="x" size={11} color="var(--ink3)" /> Clear
              </button>
            )}
            <div style={{ flex: 1 }} />
            {!loading && (
              <span style={{ fontSize: 11, color: "var(--ink4)", fontFamily: "var(--fm)" }}>
                {rows.length} / {answers.length} records
              </span>
            )}
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ flex: 1, overflow: "auto", padding: "18px 28px 32px" }}>
          <div style={{ background: "var(--card)", borderRadius: 12, border: "1px solid var(--bdr)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,.04),0 8px 24px rgba(0,0,0,.05)" }}>
            {/* Column headers — UPDATED: added Audit Trail column */}
            <div style={{ display: "flex", alignItems: "center", background: "#f4f6fa", borderBottom: "2px solid var(--bdr)", padding: "10px 0", position: "sticky", top: 0, zIndex: 10 }}>
              <div style={{ width: 52 }} />
              {[
                { l: "Date Range", flex: 1 },
                { l: "Location", w: 130 },
                { l: "Value", w: 120 },
                { l: "Document", w: 90 },
                { l: "Answered By", w: 140 },
                { l: "Answer Date", w: 110 },
                { l: "Status", w: 105, c: "center" },
                { l: "Chat", w: 60, c: "center" },
                { l: "Audit", w: 60, c: "center" },   // ← NEW column
              ].map((col) => (
                <div
                  key={col.l}
                  style={{
                    ...(col.flex ? { flex: col.flex } : { width: col.w, flexShrink: 0 }),
                    padding: "0 10px",
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: "var(--ink4)",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    fontFamily: "var(--fm)",
                    textAlign: col.c || "left",
                  }}
                >
                  {col.l}
                </div>
              ))}
              <div style={{ width: 44 }} />
            </div>

            {loading && (
              <>
                {[1, 2, 3, 4, 5].map((i) => <ShimmerRow key={i} i={i} />)}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 14, color: "var(--ink4)", fontSize: 11.5, borderTop: "1px solid var(--bdr2)" }}>
                  <span className="ahp-spin" style={{ width: 13, height: 13, border: "2px solid var(--bdr)", borderTopColor: "var(--blue)" }} />
                  Loading answer history…
                </div>
              </>
            )}

            {!loading && error && (
              <div style={{ padding: "56px 24px", textAlign: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--red)", marginBottom: 7 }}>Failed to load history</div>
                <div style={{ color: "var(--ink4)", fontSize: 12.5, maxWidth: 380, margin: "0 auto 20px" }}>{error}</div>
                <button className="ahp-btn ahp-btn-g" onClick={() => loadData({ fyIdParam: fyId, locationIdsParam: locationIds, fromDateParam: fromDate, toDateParam: toDate })}>
                  <KpiIcon name="history" size={12} color="var(--ink3)" /> Retry
                </button>
              </div>
            )}

            {!loading && !error && rows.length === 0 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 24px", gap: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink2)", marginTop: 6 }}>
                  {answers.length === 0 ? "No history yet" : "No records match filters"}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--ink4)", textAlign: "center", maxWidth: 260 }}>
                  {answers.length === 0 ? "No answers have been submitted for this KPI yet." : "Try adjusting your filters."}
                </div>
                {hasFilters && (
                  <button className="ahp-btn ahp-btn-g" onClick={clearAll} style={{ marginTop: 8 }}>Clear filters</button>
                )}
              </div>
            )}

            {/* UPDATED: pass financialYearId down to HistoryRow */}
            {!loading && !error && rows.map((ans, idx) => (
              <HistoryRow
                key={ans.id || idx}
                answer={ans}
                idx={idx}
                locs={locs}
                questionId={questionId}
                financialYearId={fyId || ans.financial_year_id}   // ← NEW
                isEven={idx % 2 === 0}
                onSave={() => loadData({ fyIdParam: fyId, locationIdsParam: locationIds, fromDateParam: fromDate, toDateParam: toDate })}
                canEdit={canAddAndEditRecords}
                onChat={() => handleOpenChat(ans)}
                isChatOpen={chatItem?.id === ans.id}
                unitSetting={unitSetting}
              />
            ))}

            {!loading && !error && rows.length > 0 && (
              <div style={{ padding: "11px 20px", borderTop: "1px solid var(--bdr2)", background: "#f8f9fb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 11, color: "var(--ink4)", fontFamily: "var(--fm)" }}>
                  Showing {rows.length} of {answers.length} records
                </span>
                <div style={{ display: "flex", gap: 14 }}>
                  {[
                    { c: "var(--green)", v: accepted, l: "accepted" },
                    { c: "var(--amber)", v: pending, l: "pending" },
                    ...(rejected > 0 ? [{ c: "var(--red)", v: rejected, l: "rejected" }] : []),
                  ].map(({ c, v, l }) => (
                    <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: c, fontWeight: 600 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c, display: "inline-block", flexShrink: 0 }} />
                      {v} {l}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <ChatModal
        show={!!chatItem && !chatLoading}
        onHide={handleCloseChat}
        title={chatTitle}
        currentUserId={currentUserId}
        userForRole={chatUserForRole ?? currentUser ?? {}}
        questionId={chatModalProps.questionId}
        financialYearId={chatModalProps.financialYearId}
        sourceId={chatModalProps.sourceId}
        subLocationId={chatModalProps.subLocationId}
        fromDate={chatModalProps.fromDate}
        toDate={chatModalProps.toDate}
        participants={chatParticipants}
        initialMessages={chatMessages}
        onSend={handleSendMessage}
      />
    </div>
  );
};

export default AnswerHistoryPage;